/**
 * Alshallal Pastry Company HR System - Leave Approvals Page
 * Phase 4: Leave Management
 * Manager/HR view for pending approvals
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  User,
  Building,
  Eye,
  Paperclip,
  Loader2
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useListViewState } from '../../hooks/useListViewState';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { LeaveAttachmentList } from '@/components/leave/LeaveAttachments';
import { LeaveRequestDetailsDialog } from '@/components/leave/LeaveRequestDetailsDialog';
import { ManagerDeleteLeaveButton } from '@/components/leave/ManagerDeleteLeaveButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LeaveApprovalsPage = () => {
  const { token, hasRole, user } = useAuth();
  const { isRTL, language } = useLanguage();

  // State
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination — the page lives in the URL so leaving this screen and coming
  // back (or a refresh) returns to the same page instead of page 1.
  const [view, setView] = useListViewState('leave-approvals', { page: 1 });
  const { page } = view;
  const setPage = useCallback((next) => setView({ page: typeof next === 'function' ? next(page) : next }), [setView, page]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Action dialog
  const [actionDialog, setActionDialog] = useState({ open: false, request: null, action: null });
  const [actionReason, setActionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Read-only details, so a sick note can be read before deciding.
  const [detailsDialog, setDetailsDialog] = useState({ open: false, request: null });

  const isHR = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');
  const isManager = hasRole('manager');

  // Fetch pending approvals
  const fetchPendingApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/leave/approvals/pending?page=${page}&page_size=15`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRequests(response.data.requests || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  // Process approval action
  const handleAction = async () => {
    if (!actionDialog.request) return;
    if (actionReason.trim().length < 10) return;

    try {
      setSubmitting(true);

      const endpoint = isHR
        ? `${API_URL}/leave/requests/${actionDialog.request.id}/hr-action`
        : `${API_URL}/leave/requests/${actionDialog.request.id}/manager-action`;

      await axios.post(endpoint, {
        action: actionDialog.action,
        reason: actionReason || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setActionDialog({ open: false, request: null, action: null });
      setActionReason('');
      fetchPendingApprovals();
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="leave-approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'موافقات الإجازات' : 'Leave Approvals'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isHR
              ? (isRTL ? 'طلبات بانتظار موافقة الموارد البشرية' : 'Requests pending HR approval')
              : (isRTL ? 'طلبات فريقك بانتظار الموافقة' : 'Team requests pending your approval')}
          </p>
        </div>
        <Button variant="outline" onClick={fetchPendingApprovals} disabled={loading}>
          <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">{total}</p>
                <p className="text-sm text-amber-600">{isRTL ? 'بانتظار المراجعة' : 'Pending Review'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {isRTL ? 'لا توجد طلبات معلقة' : 'No Pending Requests'}
              </h3>
              <p className="text-slate-600">
                {isRTL ? 'جميع طلبات الإجازة تمت معالجتها' : 'All leave requests have been processed'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead>{isRTL ? 'القسم' : 'Department'}</TableHead>
                    <TableHead>{isRTL ? 'نوع الإجازة' : 'Leave Type'}</TableHead>
                    <TableHead>{isRTL ? 'التاريخ' : 'Dates'}</TableHead>
                    <TableHead>{isRTL ? 'الأيام' : 'Days'}</TableHead>
                    <TableHead>{isRTL ? 'السبب' : 'Reason'}</TableHead>
                    <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id} data-testid={`request-row-${request.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{request.employee_name?.trim() || 'Unknown'}</span>
                          {request.attachment_count > 0 && (
                            <Badge
                              variant="outline"
                              className="gap-1 border-blue-200 bg-blue-50 text-blue-700"
                              title={isRTL ? 'يوجد مستند مرفق' : 'A supporting document is attached'}
                              data-testid={`leave-attachment-flag-${request.id}`}
                            >
                              <Paperclip className="h-3 w-3" />
                              {request.attachment_count}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-slate-400" />
                          <span>{request.employee_department || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {language === 'ar' && request.leave_type_name_ar ? request.leave_type_name_ar : request.leave_type_name}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{formatDate(request.start_date)}</p>
                          <p className="text-slate-500">→ {formatDate(request.end_date)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{request.working_days_requested}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={request.reason}>
                        {request.reason}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDetailsDialog({ open: true, request })}
                            title={isRTL ? 'عرض التفاصيل والمرفقات' : 'View details and documents'}
                            data-testid={`leave-view-btn-${request.id}`}
                          >
                            <Eye className="h-4 w-4 me-1" />
                            {isRTL ? 'عرض' : 'View'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => setActionDialog({ open: true, request, action: 'approve' })}
                            data-testid={`approve-btn-${request.id}`}
                          >
                            <CheckCircle className="h-4 w-4 me-1" />
                            {isRTL ? 'موافقة' : 'Approve'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setActionDialog({ open: true, request, action: 'reject' })}
                            data-testid={`reject-btn-${request.id}`}
                          >
                            <XCircle className="h-4 w-4 me-1" />
                            {isRTL ? 'رفض' : 'Reject'}
                          </Button>
                          {/* Only the routed manager; the server checks the team again. */}
                          {user?.employee_id && request.manager_id === user.employee_id && (
                            <ManagerDeleteLeaveButton request={request} isRTL={isRTL} onDeleted={fetchPendingApprovals} />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-600">
                    {isRTL ? `عرض ${requests.length} من ${total}` : `Showing ${requests.length} of ${total}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Read-only details — the supporting document is readable here, before
          any decision is taken. */}
      <LeaveRequestDetailsDialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, request: null })}
        request={detailsDialog.request}
        token={token}
        isRTL={isRTL}
        language={language}
        footer={detailsDialog.request && (
          <>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => {
                const request = detailsDialog.request;
                setDetailsDialog({ open: false, request: null });
                setActionDialog({ open: true, request, action: 'approve' });
              }}
              data-testid="leave-details-approve"
            >
              <CheckCircle className="h-4 w-4 me-1" />
              {isRTL ? 'موافقة' : 'Approve'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                const request = detailsDialog.request;
                setDetailsDialog({ open: false, request: null });
                setActionDialog({ open: true, request, action: 'reject' });
              }}
              data-testid="leave-details-reject"
            >
              <XCircle className="h-4 w-4 me-1" />
              {isRTL ? 'رفض' : 'Reject'}
            </Button>
          </>
        )}
      />

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, request: null, action: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={actionDialog.action === 'approve' ? 'text-green-600' : 'text-red-600'}>
              {actionDialog.action === 'approve'
                ? (isRTL ? 'الموافقة على الطلب' : 'Approve Request')
                : (isRTL ? 'رفض الطلب' : 'Reject Request')}
            </DialogTitle>
            {actionDialog.request && (
              <DialogDescription>
                {actionDialog.request.employee_name?.trim()} - {actionDialog.request.working_days_requested} {isRTL ? 'أيام' : 'days'}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 py-4">
            {actionDialog.request && (
              <LeaveAttachmentList
                token={token}
                requestId={actionDialog.request.id}
                isRTL={isRTL}
              />
            )}

            {actionDialog.action === 'reject' ? (
              <div className="space-y-2">
                <Label>{isRTL ? 'سبب الرفض (مطلوب)' : 'Rejection Reason (required)'}</Label>
                <Textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={isRTL ? 'أدخل سبب الرفض (10 أحرف على الأقل)...' : 'Enter rejection reason (min 10 characters)...'}
                  rows={3}
                  data-testid="action-reason-input"
                />
                {actionReason.length > 0 && actionReason.length < 10 && (
                  <p className="text-sm text-red-500">
                    {isRTL ? `${10 - actionReason.length} أحرف متبقية` : `${10 - actionReason.length} more characters needed`}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{isRTL ? 'سبب الموافقة (مطلوب)' : 'Approval reason (at least 10 characters)'}</Label>
                <Textarea
                  data-testid="approval-reason-input"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={isRTL ? 'أضف ملاحظات...' : 'Add notes...'}
                  rows={2}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, request: null, action: null })}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleAction}
              disabled={submitting || actionReason.trim().length < 10}
              className={actionDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-action-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 me-2 animate-spin" />{isRTL ? 'جاري المعالجة...' : 'Processing...'}</>
              ) : (
                actionDialog.action === 'approve'
                  ? (isRTL ? 'موافقة' : 'Approve')
                  : (isRTL ? 'رفض' : 'Reject')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveApprovalsPage;
